import { captureException } from "@root/tablescape/utilities/error";
import { type SWRConfiguration } from "swr";

export const STOREFRONT_TRANSITION_DURATION = "duration-300";
export const STOREFRONT_TRANSITION_EASING = "ease-in-out";

export const IMAGE_LOAD_FILTERS = ["opacity-70"];
export const MAIN_PRODUCT_CLASS = "main";
export const SUPPLEMENTAL_PRODUCT_CLASS = "supplemental";
export const RANGE_SLIDER_THUMB_WIDTH_PIXEL = 24;

export const LOOP_RETURNS_API_URL = `https://api.loopreturns.com/api/v1/cart/`;
export const OUR_PLACE_DATA_API_URL = `https://api.ourplacedata.com/graphql`;
export const YOTPO_REVIEWS_API_URL = `https://api-cdn.yotpo.com/v1/widget`;

export const LEGACY_CART_API_URL = `${
  typeof window !== "undefined" ? window.Shopify.routes?.root ?? "/" : "/"
}cart.js`;
export const LEGACY_CART_ADD_API_URL = `${
  typeof window !== "undefined" ? window.Shopify.routes?.root ?? "/" : "/"
}cart/add.js`;
export const LEGACY_CART_CLEAR_API_URL = `${
  typeof window !== "undefined" ? window.Shopify.routes?.root ?? "/" : "/"
}cart/clear.js`;
export const LEGACY_CART_UPDATE_API_URL = `${
  typeof window !== "undefined" ? window.Shopify.routes?.root ?? "/" : "/"
}cart/update.js`;

export const STOREFRONT_CART_LOCAL_STORAGE_KEY = "shopifyCartId";
export const STOREFRONT_CART_ATTRIBUTE_SYNC_KEY = "legacyCartSync";

/**
 * Storefront API
 */

/**
 * This access token is unique per storefront
 * Use `bun shopkeeper store switch <storefront>` to switch between storefronts
 */
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const STOREFRONT_ACCESS_TOKEN = process.env.STOREFRONT_ACCESS_TOKEN!;

/**
 * This URL is defined in our repository at <root>/.env.public
 * It will be periodically updated after version releases
 */
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const STOREFRONT_API_URL = process.env.STOREFRONT_API_URL!;
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
export const STOREFRONT_API_VERSION = process.env.STOREFRONT_API_VERSION!;

export enum STOREFRONT_CART_ATTRIBUTES {
  BUNDLES = "_bundles",
  GIFT_WITH_PURCHASE = "_gift_with_purchase",
  INTELLIGEMS_ID = "igId",
  INTELLIGEMS_PREVIEW = "igPreview",
  INTELLIGEMS_PRICE = "_igp",
  INTELLIGEMS_TESTGROUPS = "igTestGroups",
  MSRP = "_msrp",
  PDQ_USER_UUID = "pdq_user_uuid",
  PREORDER = "_preorder",
  RELATED_ITEMS = "_related_items",
  STANDALONE_BUNDLE_KIT = "_standalone_bundle_kit",
  TIMESTAMP = "_timestamp",
  TOTAL_WITHOUT_BMSM = "_total_without_bmsm",
  UPSELL = "_upsell",
}

export enum STOREFRONT_CART_GIFT_CARD_ATTRIBUTES {
  EMAIL = "email",
  ID = "_gift_id",
  MESSAGE = "message",
  NAME = "name",
}

export enum SHOPIFY_GIFT_CARD_PROPERTIES {
  MESSAGE = "Message",
  RECIPIENT_EMAIL = "Recipient email",
  RECIPIENT_NAME = "Recipient name",
  SEND_GIFT_CARD_TO_RECIPIENT = "__shopify_send_gift_card_to_recipient",
  SEND_ON = "Send on",
}

/** Max length for gift card personal message (Shopify limit). */
export const GIFT_CARD_MESSAGE_CHARACTER_LIMIT = 200;

export enum CART_GIFT_WITH_PURCHASE_ATTRIBUTE_VALUE_KEYS {
  CART_LINE_ID = "cartLineId",
  CREATED_AT = "createdAt",
  GIFT_BADGE = "cartSpendingGiftBadge",
  IS_REMOVABLE = "isRemovable",
  PARENT_VARIANT_ID = "parentVariantId",
  SPENDING_THRESHOLD = "cartSpendingGiftThreshold",
}

export enum SelectedVariantAvailability {
  IN_STOCK = "in-stock",
  SOLD_OUT = "sold-out",
}

export const NON_EDITABLE_PRODUCT_VENDORS = ["Onward"];

export const EMPTY_ADD_TO_CART_METADATA = {
  addonVariantIds: undefined,
  location: undefined,
  pageSection: undefined,
  pageType: undefined,
  pickYourOwn: null,
  relatedCartLineId: null,
  transformMode: null,
};

export const CartLocation = {
  // Add locations
  PDP_PRODUCT_HERO: "pdp_product_hero",
  PDP_PRODUCT_FORM: "pdp_product_form",
  PDP_COMBO_SELECTOR: "pdp_combo_selector",
  PDP_STICKY_BAR: "pdp_sticky_bar",
  PDP_HIGHLIGHTED_UPSELL: "pdp_highlighted_upsell",
  PDP_ADD_ON: "pdp_add_on",
  QUICK_ADD: "quick_add",
  UPSELL_SLIDEOUT: "upsell_slideout",
  CART_VARIANT_DRAWER: "cart_variant_drawer",
  GIFT_CARD_DRAWER: "gift_card_drawer",
  GIFT_CARD_SELECTOR: "gift_card_selector",
  TOLSTOY_CAROUSEL: "tolstoy_carousel",
  GWP_AUTO_ADD: "gwp_auto_add",
  AUTOMATED_TRANSFORM: "automated_transform",
  ADD_TO_CART_BUTTON: "add_to_cart_button",
  // Remove locations (CART_VARIANT_DRAWER reused from add side)
  CART_DRAWER_REMOVE_BUTTON: "cart_drawer_remove_button",
  GWP_REMOVE_GIFT: "gwp_remove_gift",
  GWP_THRESHOLD_LOSS: "gwp_threshold_loss",
  AUTO_REMOVE_ONWARD: "auto_remove_onward",
} as const;

export type CartLocationValue =
  (typeof CartLocation)[keyof typeof CartLocation];

export const SWR_SETTINGS: SWRConfiguration = {
  errorRetryCount: 3,
  focusThrottleInterval: 60000,
  keepPreviousData: true,
  loadingTimeout: 3000,
  onError(err: Error) {
    captureException(err, {
      tags: {
        "issue.type": "fetch",
      },
    });
  },
};

export const AVAILABLE_MARKETS = {
  AU: {
    currency: "AUD",
    isoCode: "AU",
    country: "Australia",
    languages: [
      {
        domain: "fromourplace.au",
        name: "English",
        value: "en",
      },
    ],
  },
  CA: {
    currency: "CAD",
    isoCode: "CA",
    country: "Canada",
    languages: [
      {
        domain: "fromourplace.ca",
        name: "English",
        value: "en",
      },
      {
        domain: "fromourplace.ca/fr",
        name: "Français",
        value: "fr",
      },
    ],
  },
  US: {
    country: "United States",
    currency: "USD",
    isoCode: "US",
    languages: [
      {
        domain: "fromourplace.com",
        name: "English",
        value: "en",
      },
    ],
  },
  GB: {
    currency: "GBP",
    isoCode: "GB",
    country: "United Kingdom",
    languages: [
      {
        domain: "fromourplace.co.uk",
        name: "English",
        value: "en",
      },
    ],
  },
  FR: {
    currency: "EUR",
    isoCode: "FR",
    country: "France",
    languages: [
      {
        domain: "fromourplace.fr",
        name: "Français",
        value: "fr",
      },
      {
        domain: "fromourplace.fr/en",
        name: "English",
        value: "en",
      },
    ],
  },
} as const;

type MARKET_KEYS = keyof typeof AVAILABLE_MARKETS;

export type MarketCountry = (typeof AVAILABLE_MARKETS)[MARKET_KEYS]["country"];
export type MarketCurrency =
  (typeof AVAILABLE_MARKETS)[MARKET_KEYS]["currency"];
export type MarketIsoCode = (typeof AVAILABLE_MARKETS)[MARKET_KEYS]["isoCode"];
export type MarketLanguage =
  (typeof AVAILABLE_MARKETS)[MARKET_KEYS]["languages"][number];

export const COUNTRY_FLAG_ICONS = {
  AU: "flag-australia",
  CA: "flag-canada",
  GB: "flag-united-kingdom",
  US: "flag-united-states",
  FR: "flag-france",
} as const;
